/**
 * WindowManagerContext — RUNTIME STATE for the unified window engine: which
 * floating windows are open, which tools are embedded in the hub, and the
 * display mode. State persists to localStorage so choices survive a reload.
 *
 * Generalized from the kit's old `ui/debug/DebugToolsContext.tsx` (Phase 1 of
 * the UI-framework arc). The persistence key remains `debug_tools_v2`; the
 * payload now carries independent `widgets` and `hubWidgets` maps. Legacy
 * shared state is migrated according to its saved surface so users keep their
 * selections without hub rows spawning duplicate floating windows.
 *
 * This module owns state ONLY. The other halves of the engine live apart:
 *   - registry.ts — the static catalogue (WINDOW_REGISTRY) + WindowId union
 *     + WindowDef discriminated union + categories. Pure data, no React.
 *   - content.ts  — CONTENT_BY_ID, the merged id→component binding.
 *
 * State:
 *   - `widgets[id].enabled` controls explicit floating windows.
 *   - `hubWidgets[id].enabled` controls embedded hub sections.
 *   - `displayMode.kind` selects the visible tool surface ('floating' | 'hub').
 *   - Persistence under `debug_tools_v2`. v1 state is auto-migrated on first
 *     load. Per-window URL query-params (?latency=1, ?net=1, …) seed the
 *     initial state only when no persisted state exists. A named
 *     `?dbgPreset=` is an explicit reproducibility command and overrides the
 *     persisted layout on the debug surface.
 *   - `/debug` route NO LONGER auto-enables anything (per DJ): everything is
 *     off by default; the user opens what they need via WindowLauncher.
 *
 * Hooks:
 *   - useWindow(id) → { open, toggle, openWindow, closeWindow }
 *   - useWindows() → registry list + widgets + displayMode + bulk ops
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { storage } from '../../services/StorageService';
import { isDebugSurface } from '../../../shared/diagnostics/isDebugSurface';
import { findDebugPreset } from '../../../config/debug/debugPresets';
import { WINDOW_REGISTRY } from './registry';
import type { WindowDef, WindowId } from './types';
import { windowBridge, restoreOpenWindow } from './windowBridge';
import { bootSearch, bootSearchParams } from '../../../config/boot/bootUrl';

// ── State types ────────────────────────────────────────────────────

export type DisplayModeKind = 'floating' | 'hub';
type StoredDisplayModeKind = DisplayModeKind | 'both';

interface WidgetState {
  enabled: boolean;
}

type WidgetsState = Record<WindowId, WidgetState>;

interface WindowManagerState {
  widgets: WidgetsState;
  hubWidgets: WidgetsState;
  displayMode: { kind: DisplayModeKind };
}

const STORAGE_KEY = 'debug_tools_v2';

const ALL_DISABLED: WidgetsState = WINDOW_REGISTRY.reduce(
  (acc, w) => ({ ...acc, [w.id]: { enabled: false } }),
  {} as WidgetsState,
);

function hydrateWidgets(saved?: Partial<WidgetsState>): WidgetsState {
  const hydrated = { ...ALL_DISABLED };
  if (!saved) return hydrated;
  for (const win of WINDOW_REGISTRY) {
    const state = saved[win.id];
    if (state && typeof state.enabled === 'boolean') {
      hydrated[win.id] = { enabled: state.enabled };
    }
  }
  return hydrated;
}

// ── v1 → v2 migration ──────────────────────────────────────────────

function migrateV1ToV2(): WindowManagerState | null {
  if (typeof window === 'undefined') return null;
  try {
    const v1Raw = storage.getObjectRaw<Record<string, { open?: boolean }>>('debug_tools_v1');
    if (!v1Raw) return null;

    const modeRaw = storage.getObjectRaw<{ mode?: string }>('debug_tools_mode_v1');
    const hubRaw = storage.getObjectRaw<{ open?: boolean }>('debug_tools_hub_open_v1');

    const wasSuite = modeRaw?.mode === 'suite';
    const hubWasOpen = hubRaw?.open === true;

    const savedWidgets: WidgetsState = { ...ALL_DISABLED };
    for (const win of WINDOW_REGISTRY) {
      const saved = v1Raw[win.id];
      if (saved && typeof saved.open === 'boolean') {
        savedWidgets[win.id] = { enabled: saved.open };
      }
    }

    // V1 used one selection map for both presentations. If its hub was open,
    // preserve those selections in the hub and start with no floating copies.
    if (wasSuite || hubWasOpen) {
      return {
        widgets: { ...ALL_DISABLED },
        hubWidgets: savedWidgets,
        displayMode: { kind: 'hub' },
      };
    }
    return {
      widgets: savedWidgets,
      hubWidgets: { ...ALL_DISABLED },
      displayMode: { kind: 'floating' },
    };
  } catch {
    return null;
  }
}

// ── URL seeding ────────────────────────────────────────────────────

/**
 * Seed initial open-state from query params. Each window declares its seed
 * param names in the registry (`urlSeed`); a window opens when ANY of its
 * params equals '1'. This reproduces the kit's exact seedFromUrl table (e.g.
 * netTraffic ← ?net=1 OR ?netshow=1, authorityOverlay ← ?netobsOverlay=1).
 */
function seedFromUrl(): WidgetsState {
  if (typeof window === 'undefined') return { ...ALL_DISABLED };
  let sp: URLSearchParams;
  try {
    sp = bootSearchParams();
  } catch {
    return { ...ALL_DISABLED };
  }
  const out = { ...ALL_DISABLED };
  for (const win of WINDOW_REGISTRY) {
    const params = win.urlSeed ?? [];
    const enabled = params.some((p) => sp.get(p) === '1');
    out[win.id] = { enabled };
  }
  // ?dbgPreset=<id> — seed a whole task-scoped kit at boot (debugPresets
  // table; DJ 2026-07-11). Additive with per-window flags: the preset
  // enables its set, explicit ?latency=1 etc. still stack on top.
  const preset = findDebugPreset(sp.get('dbgPreset'));
  if (preset) {
    for (const id of preset.windows) out[id] = { enabled: true };
  }
  return out;
}

/**
 * `?dbgTools=a,b,c` is the shareable custom-hub equivalent of a named preset.
 * It is exact (not additive), ignores unknown ids, and treats an empty value as
 * a deliberate empty hub. Returning null means the parameter was absent.
 */
function seedCustomHubFromUrl(search?: string): WidgetsState | null {
  if (typeof window === 'undefined') return null;
  try {
    const sp = new URLSearchParams(search ?? bootSearch());
    if (!sp.has('dbgTools')) return null;
    const requested = new Set(
      (sp.get('dbgTools') ?? '')
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean),
    );
    const out = { ...ALL_DISABLED };
    for (const win of WINDOW_REGISTRY) {
      if (requested.has(win.id)) out[win.id] = { enabled: true };
    }
    return out;
  } catch {
    return null;
  }
}

function loadInitialState(): WindowManagerState {
  // Everything starts in `floating` mode (DJ 2026-05-29: the docked Netcode +
  // Debug Hub was auto-loading on a fresh/private window). `floating` renders no
  // hub container and no windows until something is enabled — you summon the hub
  // (or any window) from the 🛠 launcher. Persisted hub mode restores only the
  // hub; legacy `both` state is normalized below.
  if (!isDebugSurface()) {
    return {
      widgets: seedFromUrl(),
      hubWidgets: { ...ALL_DISABLED },
      displayMode: { kind: 'floating' },
    };
  }

  // A named preset in the URL is an explicit operator/test instruction, not a
  // weak first-run hint. Apply it before persisted state so the canonical VQA
  // URL remains deterministic even when the isolated browser previously used
  // floating tools. Per-window flags stay additive through seedFromUrl().
  try {
    const params = bootSearchParams();
    const customHub = seedCustomHubFromUrl();
    const preset = findDebugPreset(params.get('dbgPreset'));
    if (customHub || preset) {
      return {
        widgets: { ...ALL_DISABLED },
        hubWidgets: customHub ?? seedFromUrl(),
        displayMode: { kind: 'hub' },
      };
    }
  } catch { /* malformed/unavailable location: continue to persisted state */ }

  // 1. Try v2 persisted state.
  const v2 = storage.getObjectRaw<{
    widgets?: Partial<WidgetsState>;
    hubWidgets?: Partial<WidgetsState>;
    displayMode?: { kind?: StoredDisplayModeKind };
  }>(STORAGE_KEY);
  if (v2?.widgets || v2?.hubWidgets) {
    const savedKind = v2.displayMode?.kind;
    const kind: DisplayModeKind = savedKind === 'hub' || savedKind === 'both'
      ? 'hub'
      : 'floating';

    if (v2.hubWidgets) {
      return {
        widgets: hydrateWidgets(v2.widgets),
        hubWidgets: hydrateWidgets(v2.hubWidgets),
        displayMode: { kind },
      };
    }

    // First load after the hub/floating decoupling. The old payload had one
    // shared map. Route it to the saved surface; hub/both becomes clean hub
    // mode with no duplicate floating cards.
    const legacyWidgets = hydrateWidgets(v2.widgets);
    return kind === 'hub'
      ? {
          widgets: { ...ALL_DISABLED },
          hubWidgets: legacyWidgets,
          displayMode: { kind: 'hub' },
        }
      : {
          widgets: legacyWidgets,
          hubWidgets: { ...ALL_DISABLED },
          displayMode: { kind: 'floating' },
        };
  }

  // 2. Try v1 → v2 migration.
  const migrated = migrateV1ToV2();
  if (migrated) return migrated;

  // 3. Seed from per-window URL params. Named presets already returned above;
  // individual flags without a preset retain the floating-window behavior.
  const seeded = seedFromUrl();
  return {
    widgets: seeded,
    hubWidgets: { ...ALL_DISABLED },
    displayMode: { kind: 'floating' },
  };
}

// ── Context ────────────────────────────────────────────────────────

export interface WindowHandle {
  open: boolean;
  toggle: () => void;
  openWindow: () => void;
  closeWindow: () => void;
}

interface WindowManagerContextValue {
  widgets: WidgetsState;
  hubWidgets: WidgetsState;
  windows: readonly WindowDef[];
  setEnabled: (id: WindowId, enabled: boolean) => void;
  setHubEnabled: (id: WindowId, enabled: boolean) => void;
  setHubMany: (windowIds: readonly WindowId[], enabled: boolean) => void;
  toggle: (id: WindowId) => void;
  closeAll: () => void;
  openAll: () => void;
  closeAllHub: () => void;
  openAllHub: () => void;
  /** Atomic hub preset swap: everything embedded off, exactly these on. */
  applyHubPreset: (windowIds: readonly WindowId[]) => void;
  displayMode: { kind: DisplayModeKind };
  setDisplayMode: (kind: DisplayModeKind) => void;
}

const WindowManagerContext = createContext<WindowManagerContextValue | null>(null);

export const WindowManagerProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [initialState] = useState(loadInitialState);
  const [widgets, setWidgets] = useState<WidgetsState>(initialState.widgets);
  const [hubWidgets, setHubWidgets] = useState<WidgetsState>(initialState.hubWidgets);
  const [displayMode, setDisplayModeState] = useState<{ kind: DisplayModeKind }>(
    initialState.displayMode,
  );

  useEffect(() => {
    storage.setObjectRaw(STORAGE_KEY, { widgets, hubWidgets, displayMode });
  }, [widgets, hubWidgets, displayMode]);

  const setDisplayMode = useCallback((kind: DisplayModeKind) => {
    setDisplayModeState({ kind });
  }, []);

  const setEnabled = useCallback((id: WindowId, enabled: boolean) => {
    setWidgets((prev) => (prev[id]?.enabled === enabled ? prev : { ...prev, [id]: { enabled } }));
  }, []);

  const setHubEnabled = useCallback((id: WindowId, enabled: boolean) => {
    setHubWidgets((prev) => (prev[id]?.enabled === enabled ? prev : { ...prev, [id]: { enabled } }));
  }, []);

  const setHubMany = useCallback((windowIds: readonly WindowId[], enabled: boolean) => {
    setHubWidgets((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const id of windowIds) {
        if (next[id]?.enabled !== enabled) {
          next[id] = { enabled };
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, []);

  const toggle = useCallback((id: WindowId) => {
    setWidgets((prev) => ({ ...prev, [id]: { enabled: !prev[id]?.enabled } }));
  }, []);

  // Wire the imperative bridge for non-React callers (/tracker, action dispatcher).
  useEffect(() => {
    const open = (id: WindowId) => { setEnabled(id, true); restoreOpenWindow(id); };
    const close = (id: WindowId) => { setEnabled(id, false); };
    windowBridge.toggle = toggle;
    windowBridge.open = open;
    windowBridge.close = close;

    try {
      if (typeof window !== 'undefined') {
        const sp = bootSearchParams();
        if (sp.get('openBank') === '1' || sp.get('bank') === '1') {
          open('bank');
        }
        if (sp.get('openJobs') === '1' || sp.get('jobs') === '1') {
          open('jobs');
        }
        if (sp.get('openSpells') === '1' || sp.get('spells') === '1') {
          open('spells');
        }
      }
    } catch { /* SSR / test guard */ }

    return () => {
      if (windowBridge.toggle === toggle) windowBridge.toggle = () => {};
      if (windowBridge.open === open) windowBridge.open = () => {};
      if (windowBridge.close === close) windowBridge.close = () => {};
    };
  }, [toggle, setEnabled]);

  const closeAll = useCallback(() => {
    setWidgets(() => ({ ...ALL_DISABLED }));
  }, []);

  const closeAllHub = useCallback(() => {
    setHubWidgets(() => ({ ...ALL_DISABLED }));
  }, []);

  const applyHubPreset = useCallback((windowIds: readonly WindowId[]) => {
    // One atomic state swap (NOT closeAll + N setEnabled — sequential
    // functional updates would churn N re-renders and race persistence).
    setHubWidgets(() => {
      const next = { ...ALL_DISABLED };
      for (const id of windowIds) next[id] = { enabled: true };
      return next;
    });
  }, []);

  const openAllHub = useCallback(() => {
    setHubWidgets(() =>
      WINDOW_REGISTRY.filter((w) => w.kind !== 'gameplay').reduce(
        (acc, w) => ({ ...acc, [w.id]: { enabled: true } }),
        {} as WidgetsState,
      ),
    );
  }, []);

  const openAll = useCallback(() => {
    // Dev-sweep action: gameplay windows (Bank/Trade/Twitch iframe…) are
    // excluded — "open all" exists for debug sweeps and perf probes, and
    // opening 20 gameplay windows (plus an iframe embed) skews any
    // measurement (DJ ruling 2026-06-12). Gameplay windows open individually.
    setWidgets(() =>
      WINDOW_REGISTRY.filter((w) => w.kind !== 'gameplay').reduce(
        (acc, w) => ({ ...acc, [w.id]: { enabled: true } }),
        {} as WidgetsState,
      ),
    );
  }, []);

  const value = useMemo<WindowManagerContextValue>(
    () => ({
      widgets,
      hubWidgets,
      windows: WINDOW_REGISTRY,
      setEnabled,
      setHubEnabled,
      setHubMany,
      toggle,
      closeAll,
      openAll,
      closeAllHub,
      openAllHub,
      applyHubPreset,
      displayMode,
      setDisplayMode,
    }),
    [
      widgets,
      hubWidgets,
      setEnabled,
      setHubEnabled,
      setHubMany,
      toggle,
      closeAll,
      openAll,
      closeAllHub,
      openAllHub,
      applyHubPreset,
      displayMode,
      setDisplayMode,
    ],
  );

  return (
    <WindowManagerContext.Provider value={value}>
      {children}
    </WindowManagerContext.Provider>
  );
};

export function useWindows(): WindowManagerContextValue {
  const ctx = useContext(WindowManagerContext);
  if (!ctx) {
    throw new Error('useWindows must be used inside <WindowManagerProvider>');
  }
  return ctx;
}

export function useWindow(id: WindowId): WindowHandle {
  const { widgets, setEnabled, toggle } = useWindows();
  const open = widgets[id]?.enabled ?? false;
  return useMemo<WindowHandle>(
    () => ({
      open,
      toggle: () => toggle(id),
      openWindow: () => setEnabled(id, true),
      closeWindow: () => setEnabled(id, false),
    }),
    [id, open, setEnabled, toggle],
  );
}

// Internal export for tests.
export const __INTERNAL__ = {
  ALL_DISABLED,
  STORAGE_KEY,
  seedFromUrl,
  seedCustomHubFromUrl,
  loadInitialState,
  migrateV1ToV2,
};
